import { ActivatedRoute, ActivatedRouteSnapshot } from '@angular/router';
import { format, parseISO } from 'date-fns';
import {
  DEFAULT_PERIOD_SELECTOR_QUERY_PARAM_KEY,
  ExplicitPeriod,
  parsePeriodParam,
} from 'src/app/models/period-selector';

export function queryParamTransform<T>(fallback: T | null = null) {
  return (value: string | string[] | null | undefined): T | null => {
    if (!value || typeof value !== 'string') return fallback;

    try {
      return JSON.parse(value) as T;
    } catch (e) {
      console.error(`Failed to parse query param: ${value}`, e);
      return fallback;
    }
  };
}

/** The `YYYY-MM-DD` shape every date query param is written and read in. */
const DATE_QUERY_PARAM_FORMAT = 'yyyy-MM-dd';

export function hasRouteParams(route: ActivatedRoute): boolean {
  if (route.snapshot.paramMap.keys.length > 0) {
    return true;
  }

  return route.firstChild ? hasRouteParams(route.firstChild) : false;
}

/**
 * Parses a date from a query parameter and returns it as "YYYY-MM-DD".
 * Falls back to the provided default value (or today if none provided) when missing or invalid.
 *
 * Both the parse and the format are **local**, matching
 * `QueryParamSignalSyncService` — `parseISO` reads a date-only param as local
 * midnight, and `format` names the local calendar day. `new Date(...)` plus
 * `toISOString()` named the *UTC* day instead, so between local midnight and
 * UTC midnight a resolver fetched a different day from the one the component's
 * date signal (formatted through local getters) displayed
 * (docs/plans/period-defaults/2026-09-09-plan.md, S4).
 *
 * @param route The activated route snapshot
 * @param paramKey The query parameter key to read (e.g. 'chartsDate')
 * @param fallback Optional fallback date string in "YYYY-MM-DD" format
 */
export function getDateFromQueryParam(
  route: ActivatedRouteSnapshot,
  paramKey: string,
  fallback?: string
): string {
  const qp = route.queryParams[paramKey];

  if (qp) {
    const parsed = parseISO(qp);
    if (!isNaN(parsed.getTime())) {
      return format(parsed, DATE_QUERY_PARAM_FORMAT);
    }
  }

  return fallback ?? format(new Date(), DATE_QUERY_PARAM_FORMAT);
}

/**
 * Parses the 'chartsDate' query parameter from the route and returns
 * a string in "YYYY-MM-DD" format. Defaults to today if missing or invalid.
 *
 * @param route The activated route snapshot
 */
export function getChartsDateFromQueryParam(
  route: ActivatedRouteSnapshot
): string {
  return getDateFromQueryParam(route, 'chartsDate');
}

/**
 * The explicit period a route's URL carries under `paramKey` — `null` when it
 * carries none. Callers answer `null` by **omitting** the request's `period`
 * param: absence is the request for the backend's default, and there is no
 * frontend fallback any more
 * (docs/plans/period-defaults/2026-09-09-plan.md, S3). Malformed values read as
 * `null` too — {@link parsePeriodParam}, the same parse the components' own URL
 * read uses, so a resolver and the chip it feeds agree.
 *
 * `'unfiltered'` comes back under either key: the chips that can be switched
 * off (the base chip, and a stat chip bound with `allowInfinitePeriod`) write
 * it, and the tile grids accept it.
 *
 * A free function, not a service method: it is a pure read of the snapshot, and
 * the ten tile-grid resolvers that call it had no other reason to inject.
 *
 * @param route - The snapshot a resolver runs against (the component does not
 * exist yet, so there is no signal to read instead).
 * @param paramKey - The URL param: `'period'` (default) or `'statPeriod'`.
 */
export function getPeriodValueFromRouteSnapshot(
  route: ActivatedRouteSnapshot,
  paramKey: string = DEFAULT_PERIOD_SELECTOR_QUERY_PARAM_KEY
): ExplicitPeriod | null {
  return parsePeriodParam(route.queryParams[paramKey]);
}
